// File: src/pages/teams/components/modals/EmailComposerModal.jsx
'use client';

import React, { useEffect, useMemo, useState, useRef, useCallback } from 'react';
import SubmitButton from '../../../../../components/forms/formComponents/SubmitButton';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../../../../firebase-client';
import '../../../teams.css';

import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
import Heading from '@tiptap/extension-heading';
import Image from '@tiptap/extension-image';
import { TableKit } from '@tiptap/extension-table';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '../../../../../firebase-client';
import EmailTemplateLibrary from './EmailTemplateLibrary';
import './emailComposer.css';
import { emailPreviewHtml } from '../../../../../utils/emailContent';
import 'prosemirror-view/style/prosemirror.css';

/* ───────────────── styles ──────────────── */
const styles = {
  tiptapWrap: {
    border: '1px solid var(--border, #e5e7eb)',
    background: '#fff',
    borderRadius: 12,
    overflow: 'hidden',
  },
  tiptapToolbar: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 6,
    padding: 8,
    borderBottom: '1px solid var(--border, #e5e7eb)',
    background: 'var(--team-secondary, #f8f9fa)',
  },
  // Clickable area around the editor that will focus it
  tiptapEditorOuter: {
    minHeight: 220,
    padding: 12,
    cursor: 'text',
  },
  // Style that applies to the ProseMirror content element itself
  tiptapEditorInner: {
    outline: 'none',
  },
};

const EMPTY_BOOKING_AUDIENCE_OPTIONS = [];

/* ───────── helpers ───────── */
function groupTagsByCategory(tags = []) {
  const normalised = Array.isArray(tags) ? tags : [];
  return normalised.reduce((acc, t) => {
    const cat = t?.category || 'Uncategorised';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(t);
    return acc;
  }, {});
}

function memberLabel(member = {}) {
  return member.name || member.displayName || member.email || 'Member';
}

function ToolbarButton({ on, onClick, children, title }) {
  return (
    <button
      type="button"
      title={title}
      aria-label={title}
      aria-pressed={Boolean(on)}
      onMouseDown={(event) => event.preventDefault()}
      onClick={onClick}
      className={`btn btn--ghost email-toolbar-button ${on ? 'btn--active' : ''}`}
      style={{ padding: '6px 10px' }}
    >
      {children}
    </button>
  );
}

function TiptapEmailEditor({ value, onChange, teamId }) {
  const [showImage, setShowImage] = useState(false);
  const [imageUrl, setImageUrl] = useState('');
  const [imageAlt, setImageAlt] = useState('');
  const [imageError, setImageError] = useState('');
  const [uploading, setUploading] = useState(false);
  const editor = useEditor({
    shouldRerenderOnTransaction: true,
    extensions: [
      StarterKit.configure({
        blockquote: true,
        codeBlock: true,
        link: false,
        underline: false,
        heading: false, // disable in StarterKit; we add Heading separately
      }),
      Heading.configure({ levels: [1, 2, 3] }),
      Underline,
      Image.configure({ HTMLAttributes: { style: 'max-width:100%;height:auto;display:block;' } }),
      TableKit.configure({
        table: { HTMLAttributes: { style: 'border-collapse:collapse;width:100%;table-layout:fixed;' } },
        tableCell: { HTMLAttributes: { style: 'border:1px solid #d1d5db;padding:8px;vertical-align:top;' } },
        tableHeader: { HTMLAttributes: { style: 'border:1px solid #d1d5db;padding:8px;text-align:left;background-color:#f3f4f6;' } },
      }),
      Link.configure({
        openOnClick: false,
        autolink: true,
        HTMLAttributes: { rel: 'noopener noreferrer' },
      }),
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
    ],
    content: value || '',
    editorProps: {
      attributes: {
        // style object as inline css string for the ProseMirror element
        style: Object.entries(styles.tiptapEditorInner)
          .map(([k, v]) => `${k.replace(/[A-Z]/g, (m) => `-${m.toLowerCase()}`)}:${v}`)
          .join(';'),
      },
    },
    onUpdate({ editor }) {
      onChange?.(editor.getHTML());
    },
  });

  useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    if ((value || '') !== current) editor.commands.setContent(value || '', { emitUpdate: false });
  }, [value, editor]);

  if (!editor)
    return (
      <div className="card" style={{ padding: '0.75rem' }}>
        Loading editor…
      </div>
    );

  const is = (name, attrs) => editor.isActive(name, attrs);
  const run = (fn) => () => fn();

  return (
    <div className="email-rich-editor" style={styles.tiptapWrap}>
      <div className="email-editor-toolbar" style={styles.tiptapToolbar}>
        <select
          aria-label="Text style"
          value={is('heading', { level: 1 }) ? '1' : is('heading', { level: 2 }) ? '2' : is('heading', { level: 3 }) ? '3' : 'paragraph'}
          onChange={(event) => {
            const level = Number(event.target.value);
            if (level) editor.chain().focus().setHeading({ level }).run();
            else editor.chain().focus().setParagraph().run();
          }}
          style={{ width: 'auto' }}
        >
          <option value="paragraph">Paragraph</option>
          <option value="1">Heading 1</option>
          <option value="2">Heading 2</option>
          <option value="3">Heading 3</option>
        </select>
        <ToolbarButton on={is('bold')} onClick={run(() => editor.chain().focus().toggleBold().run())} title="Bold">
          B
        </ToolbarButton>
        <ToolbarButton on={is('italic')} onClick={run(() => editor.chain().focus().toggleItalic().run())} title="Italic">
          I
        </ToolbarButton>
        <ToolbarButton on={is('underline')} onClick={run(() => editor.chain().focus().toggleUnderline().run())} title="Underline">
          U
        </ToolbarButton>
        <ToolbarButton on={is('orderedList')} onClick={run(() => editor.chain().focus().toggleOrderedList().run())} title="Numbered list">
          1.
        </ToolbarButton>
        <ToolbarButton on={is('bulletList')} onClick={run(() => editor.chain().focus().toggleBulletList().run())} title="Bullet list">
          •
        </ToolbarButton>
        <ToolbarButton on={is('blockquote')} onClick={run(() => editor.chain().focus().toggleBlockquote().run())} title="Quote">
          ❝
        </ToolbarButton>
        <ToolbarButton on={is('codeBlock')} onClick={run(() => editor.chain().focus().toggleCodeBlock().run())} title="Code">
          {'</>'}
        </ToolbarButton>
        <ToolbarButton on={is({ textAlign: 'left' })} onClick={run(() => editor.chain().focus().setTextAlign('left').run())} title="Align left">
          ⟸
        </ToolbarButton>
        <ToolbarButton on={is({ textAlign: 'center' })} onClick={run(() => editor.chain().focus().setTextAlign('center').run())} title="Align center">
          ≡
        </ToolbarButton>
        <ToolbarButton on={is({ textAlign: 'right' })} onClick={run(() => editor.chain().focus().setTextAlign('right').run())} title="Align right">
          ⟹
        </ToolbarButton>
        <ToolbarButton
          on={is('link')}
          onClick={() => {
            const url = prompt('Enter URL');
            if (url === null) return;
            if (url === '') editor.chain().focus().unsetLink().run();
            else editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
          }}
          title="Insert link"
        >
          🔗
        </ToolbarButton>
        <ToolbarButton
          on={is('image')}
          title="Insert image"
          onClick={() => {
            setShowImage((current) => !current);
            setImageError('');
          }}
        >
          Image
        </ToolbarButton>
        <ToolbarButton
          on={is('table')}
          title="Insert table"
          onClick={() => editor.chain().focus().insertTable({ rows: 3, cols: 2, withHeaderRow: true }).run()}
        >
          Table
        </ToolbarButton>
        <ToolbarButton title="Undo" onClick={() => editor.chain().focus().undo().run()}>
          ↶
        </ToolbarButton>
        <ToolbarButton title="Redo" onClick={() => editor.chain().focus().redo().run()}>
          ↷
        </ToolbarButton>
        <ToolbarButton on={false} onClick={run(() => editor.chain().focus().unsetAllMarks().clearNodes().run())} title="Clear formatting">
          🧹
        </ToolbarButton>
      </div>

      {is('table') && (
        <div className="email-composer-actions email-table-actions" aria-label="Table controls">
          {[
            ['addRowAfter', 'Add row'],
            ['addColumnAfter', 'Add column'],
            ['deleteRow', 'Delete row'],
            ['deleteColumn', 'Delete column'],
            ['toggleHeaderRow', 'Header row'],
            ['deleteTable', 'Delete table'],
          ].map(([command, label]) => (
            <button key={command} type="button" className="btn btn--ghost" onClick={() => editor.chain().focus()[command]().run()}>
              {label}
            </button>
          ))}
        </div>
      )}
      {showImage && (
        <div className="email-image-form">
          <label>
            <span className="form-label">Image description (alt text)</span>
            <input value={imageAlt} onChange={(event) => setImageAlt(event.target.value)} placeholder="Describe the image for readers" />
          </label>
          <label>
            <span className="form-label">Public image URL</span>
            <input type="url" value={imageUrl} onChange={(event) => setImageUrl(event.target.value)} placeholder="https://…" />
          </label>
          <div className="email-composer-actions">
            <button
              type="button"
              className="btn btn--secondary"
              disabled={uploading}
              onClick={() => {
                try {
                  const url = new URL(imageUrl);
                  if (url.protocol !== 'https:') throw new Error();
                  editor.chain().focus().setImage({ src: url.href, alt: imageAlt }).run();
                  setShowImage(false);
                  setImageUrl('');
                } catch {
                  setImageError('Enter a public HTTPS image URL.');
                }
              }}
            >
              Insert image URL
            </button>
            <label className="btn btn--ghost">
              {uploading ? 'Uploading…' : 'Upload image'}
              <input
                className="email-image-upload"
                type="file"
                accept="image/jpeg,image/png,image/gif,image/webp"
                disabled={uploading}
                onChange={async (event) => {
                  const file = event.target.files?.[0];
                  if (!file) return;
                  if (!['image/jpeg', 'image/png', 'image/gif', 'image/webp'].includes(file.type) || file.size === 0 || file.size > 5 * 1024 * 1024) {
                    setImageError('Choose a JPEG, PNG, GIF or WebP image up to 5 MB.');
                    return;
                  }
                  setUploading(true);
                  setImageError('');
                  try {
                    const destination = ref(storage, `email-images/${teamId}/${crypto.randomUUID()}`);
                    await uploadBytes(destination, file, { contentType: file.type });
                    const src = await getDownloadURL(destination);
                    editor.chain().focus().setImage({ src, alt: imageAlt }).run();
                    setShowImage(false);
                  } catch {
                    setImageError('Could not upload the image. Please try again.');
                  } finally {
                    setUploading(false);
                  }
                }}
              />
            </label>
            <button type="button" className="btn btn--ghost" disabled={uploading} onClick={() => setShowImage(false)}>
              Cancel
            </button>
          </div>
          <p className="text-muted">
            Newsletter images are accessible to anyone with their image link. Use a description so readers can understand images when their email app hides
            them.
          </p>
          {imageError && <p role="alert">{imageError}</p>}
        </div>
      )}
      {/* Click anywhere in this padded area to focus the editor */}
      <div
        style={styles.tiptapEditorOuter}
        onMouseDown={(e) => {
          // If you click in the padding/blank area, focus the editor.
          // Prevent default so the click doesn't blur immediately.
          if (e.target === e.currentTarget) {
            e.preventDefault();
            editor.chain().focus().run();
          }
        }}
      >
        <EditorContent editor={editor} />
      </div>
    </div>
  );
}

/** Stable debounced callback that doesn't change identity across renders */
function useStableDebounced(fn, delay = 400) {
  const fnRef = useRef(fn);
  const timerRef = useRef(null);

  useEffect(() => {
    fnRef.current = fn;
  }, [fn]);

  const debounced = useCallback(
    (...args) => {
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        fnRef.current?.(...args);
      }, delay);
    },
    [delay],
  );

  useEffect(() => () => clearTimeout(timerRef.current), []);
  return debounced;
}

function friendlyError(e, fallback = 'Something went wrong.') {
  const code = e?.code || e?.message || '';
  if (typeof code === 'string') {
    if (code.includes('permission-denied')) return 'Only admins can send emails.';
    if (code.includes('failed-precondition') && code.includes('No matching members')) return 'No selected members have a deliverable email.';
    if (code.includes('invalid-argument')) return 'Please check the fields and try again.';
  }
  return fallback;
}

/* ───────── component ───────── */
export default function EmailComposerModal({
  isOpen,
  onClose,
  teamId,
  tags = [],
  members = [],
  defaultAudienceMode = 'tags',
  defaultMemberId = '',
  defaultSubject = '',
  defaultPreviewText = '',
  defaultHtml = '',
  bookingAudienceOptions = EMPTY_BOOKING_AUDIENCE_OPTIONS,
  defaultBookingAudience = null,
  onSent,
}) {
  const [subject, setSubject] = useState(defaultSubject);
  const [previewText, setPreviewText] = useState(defaultPreviewText);
  const [html, setHtml] = useState(defaultHtml);
  const [showRawHtml, setShowRawHtml] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [previewWidth, setPreviewWidth] = useState('desktop');

  const [audienceMode, setAudienceMode] = useState(defaultAudienceMode);
  const [selectedTags, setSelectedTags] = useState({});
  const [selectedMemberId, setSelectedMemberId] = useState(defaultMemberId || '');
  const [selectedBookingAudience, setSelectedBookingAudience] = useState(defaultBookingAudience);
  const [memberSearch, setMemberSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [audienceCount, setAudienceCount] = useState(null);
  const [audienceStats, setAudienceStats] = useState(null);
  const [audienceLoading, setAudienceLoading] = useState(false);
  const [audienceErr, setAudienceErr] = useState('');
  const [deliveryMode, setDeliveryMode] = useState('now');
  const [scheduledAt, setScheduledAt] = useState('');
  const [daysBefore, setDaysBefore] = useState(14);

  // reset state each time modal opens
  useEffect(() => {
    if (!isOpen) return;
    setSubject(defaultSubject || '');
    setPreviewText(defaultPreviewText || '');
    setHtml(defaultHtml || '');
    setAudienceMode(defaultAudienceMode === 'member' ? 'member' : defaultAudienceMode === 'booking' ? 'booking' : 'tags');
    setSelectedTags({});
    setSelectedMemberId(defaultMemberId || '');
    setSelectedBookingAudience(defaultBookingAudience || bookingAudienceOptions[0]?.value || null);
    setMemberSearch('');
    setTestEmail('');
    setLoading(false);
    setAudienceCount(null);
    setAudienceStats(null);
    setAudienceErr('');
    setShowRawHtml(false);
    setShowPreview(false);
    setDeliveryMode('now');
    setScheduledAt('');
    setDaysBefore(14);
  }, [isOpen, defaultAudienceMode, defaultMemberId, defaultSubject, defaultPreviewText, defaultHtml, defaultBookingAudience, bookingAudienceOptions]);
  useEffect(() => {
    if (audienceMode !== 'booking' && deliveryMode === 'relative') setDeliveryMode('now');
  }, [audienceMode, deliveryMode]);

  const grouped = useMemo(() => groupTagsByCategory(tags), [tags]);
  const sortedMembers = useMemo(
    () => [...members].filter((member) => member?.id).sort((left, right) => memberLabel(left).localeCompare(memberLabel(right))),
    [members],
  );
  const selectedMember = useMemo(() => sortedMembers.find((member) => member.id === selectedMemberId) || null, [selectedMemberId, sortedMembers]);
  const filteredMembers = useMemo(() => {
    const needle = memberSearch.trim().toLowerCase();
    if (!needle) return sortedMembers;
    return sortedMembers.filter(
      (member) =>
        memberLabel(member).toLowerCase().includes(needle) ||
        String(member?.email || '')
          .toLowerCase()
          .includes(needle),
    );
  }, [memberSearch, sortedMembers]);

  const toggleTag = (category, tagName) => {
    setSelectedTags((prev) => {
      const next = { ...prev };
      const set = new Set(next[category] || []);
      if (set.has(tagName)) set.delete(tagName);
      else set.add(tagName);
      if (set.size === 0) delete next[category];
      else next[category] = set;
      return next;
    });
  };

  const clearTags = () => setSelectedTags({});

  const selectedTagFilters = useMemo(() => {
    const out = [];
    Object.entries(selectedTags).forEach(([category, set]) => {
      [...set].forEach((name) => out.push({ category, name }));
    });
    return out;
  }, [selectedTags]);

  /* ── Stable, sorted filters to avoid identity churn ── */
  const stableSelectedTagFilters = useMemo(() => {
    const arr = selectedTagFilters.map((f) => ({
      category: f.category,
      name: f.name,
    }));
    arr.sort((a, b) => a.category.localeCompare(b.category) || a.name.localeCompare(b.name));
    return arr;
  }, [selectedTagFilters]);

  const estimateAudience = useCallback(
    async ({ all, filters, explicitMemberIds, bookingAudience }) => {
      if (!teamId) return;
      if (!all && !bookingAudience?.sourceId && (!filters || filters.length === 0) && (!explicitMemberIds || explicitMemberIds.length === 0)) {
        setAudienceCount(null);
        setAudienceStats(null);
        setAudienceErr('');
        return;
      }
      try {
        setAudienceLoading(true);
        setAudienceErr('');
        const payload = {
          teamId,
          all,
          tagFilters: filters,
          explicitMemberIds,
          bookingAudience,
          debug: false,
        };
        const estimate = httpsCallable(functions, 'estimateAudience');
        const res = await estimate(payload);
        const data = res?.data || {};
        setAudienceCount(typeof data.count === 'number' ? data.count : null);
        setAudienceStats(data);
      } catch (e) {
        setAudienceErr('Could not estimate audience.');
        setAudienceCount(null);
        setAudienceStats(null);
      } finally {
        setAudienceLoading(false);
      }
    },
    [teamId],
  );

  const debouncedEstimate = useStableDebounced(estimateAudience, 450);

  /* Request de-duplication */
  const lastEstimateKeyRef = useRef('');

  const requestEstimate = useCallback(
    (nextAudience) => {
      const key = nextAudience.bookingAudience?.sourceId
        ? `BOOKING:${JSON.stringify(nextAudience.bookingAudience)}`
        : nextAudience.all
          ? 'ALL'
          : nextAudience.explicitMemberIds?.length
            ? `MEMBER:${nextAudience.explicitMemberIds[0]}`
            : `TAGS:${JSON.stringify(nextAudience.filters)}`;
      if (lastEstimateKeyRef.current === key) return; // identical; skip
      lastEstimateKeyRef.current = key;
      debouncedEstimate(nextAudience);
    },
    [debouncedEstimate],
  );

  // Force a fresh estimate key on open
  useEffect(() => {
    if (!isOpen) return;
    lastEstimateKeyRef.current = '';
  }, [isOpen]);

  // Trigger a single debounced estimate per *real* interaction.
  useEffect(() => {
    if (!isOpen) return;
    requestEstimate({
      all: audienceMode === 'all',
      filters: audienceMode === 'tags' ? stableSelectedTagFilters : [],
      explicitMemberIds: audienceMode === 'member' && selectedMemberId ? [selectedMemberId] : [],
      bookingAudience: audienceMode === 'booking' ? selectedBookingAudience : null,
    });
  }, [audienceMode, isOpen, selectedMemberId, selectedBookingAudience, stableSelectedTagFilters, requestEstimate]);

  const validate = () => {
    if (!subject.trim()) return 'Please enter a subject.';
    const cleaned = (html || '')
      .replace(/<p>\s*<\/p>/g, '')
      .replace(/<p><br><\/p>/g, '')
      .trim();
    if (!cleaned) return 'Please enter email content.';
    if (audienceMode === 'member' && !selectedMemberId) {
      return 'Choose one member to email.';
    }
    if (audienceMode === 'tags' && stableSelectedTagFilters.length === 0) {
      return 'Pick at least one tag category/tag, or tick “All members”.';
    }
    if (audienceMode === 'booking' && !selectedBookingAudience?.sourceId) return 'Choose an event or course audience.';
    return null;
  };

  const buildAudiencePayload = () => {
    if (audienceMode === 'all') return { all: true };
    if (audienceMode === 'member') return { explicitMemberIds: [selectedMemberId] };
    if (audienceMode === 'booking') return { bookingAudience: selectedBookingAudience };
    return { tagFilters: stableSelectedTagFilters };
  };

  async function handleSendNow() {
    const err = validate();
    if (err) return alert(err);

    try {
      setLoading(true);
      const call = httpsCallable(functions, 'sendCampaign');
      const payload = {
        teamId,
        campaign: {
          subject,
          previewText: previewText || '',
          html,
          status: 'draft',
          audience: buildAudiencePayload(),
        },
        options:
          deliveryMode === 'now'
            ? { mode: 'sendNow' }
            : {
                mode: 'schedule',
                schedule:
                  deliveryMode === 'relative'
                    ? { kind: 'relative', daysBefore: Number(daysBefore || 0) }
                    : {
                        kind: 'absolute',
                        sendAt: new Date(scheduledAt).toISOString(),
                      },
              },
      };
      const res = await call(payload);
      const campaignId = res?.data?.campaignId || null;
      onSent?.({ campaignId });
      alert(deliveryMode === 'now' ? 'Email is being sent.' : 'Email scheduled. Recipients will be resolved again at send time.');
      onClose();
    } catch (e) {
      alert(friendlyError(e, 'Failed to send the email. Please try again.'));
    } finally {
      setLoading(false);
    }
  }

  async function handleTestSend() {
    if (!testEmail || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(testEmail)) {
      return alert('Enter a valid test email address.');
    }
    if (!subject.trim()) return alert('Subject is required for a test send.');
    const cleaned = (html || '')
      .replace(/<p>\s*<\/p>/g, '')
      .replace(/<p><br><\/p>/g, '')
      .trim();
    if (!cleaned) return alert('Content is required for a test send.');

    try {
      setLoading(true);
      const call = httpsCallable(functions, 'sendCampaign');
      const payload = {
        teamId,
        campaign: {
          subject,
          previewText: previewText || '',
          html,
          status: 'draft',
          audience: { explicitEmails: [testEmail] },
        },
        options: { mode: 'test' },
      };
      await call(payload);
      alert('Test email sent.');
    } catch (e) {
      alert(friendlyError(e, 'Failed to send test email.'));
    } finally {
      setLoading(false);
    }
  }

  if (!isOpen) return null;

  const sendDisabled =
    loading ||
    audienceLoading ||
    (audienceMode === 'tags' && stableSelectedTagFilters.length === 0) ||
    (audienceMode === 'member' && !selectedMemberId) ||
    (audienceMode === 'booking' && !selectedBookingAudience?.sourceId);
  const audienceLabel =
    audienceMode === 'all'
      ? 'All members'
      : audienceMode === 'member'
        ? 'Individual member'
        : audienceMode === 'booking'
          ? 'Event/course bookers'
          : 'Tag-filtered';

  return (
    <div className="modal-backdrop email-composer-backdrop" onClick={onClose}>
      <div
        className="modal-content email-composer"
        role="dialog"
        aria-modal="true"
        aria-label="Compose email"
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: 880 }}
      >
        <header className="email-composer-header">
          <h2>Compose email</h2>
          <p className="text-muted">Write your message, choose who receives it, then send when you're ready.</p>
        </header>

        <EmailTemplateLibrary
          teamId={teamId}
          content={{ subject, previewText, html }}
          onApply={(template) => {
            setSubject(template.subject);
            setPreviewText(template.previewText || '');
            setHtml(template.html);
            setShowRawHtml(true);
            setShowPreview(false);
          }}
        />

        {/* Subject + preview */}
        <div className="email-message-fields">
          <label>
            <span className="form-label">Subject</span>
            <input type="text" value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Subject" maxLength={998} />
          </label>
          <label>
            <span className="form-label">Preview text (optional)</span>
            <input
              type="text"
              value={previewText}
              onChange={(e) => setPreviewText(e.target.value)}
              placeholder="Preview text that appears in inbox snippets"
              maxLength={500}
            />
          </label>
        </div>

        {/* Body (Rich Editor) */}
        <div className="email-content-heading">
          <span className="form-label" style={{ marginBottom: 0 }}>Message</span>
          <div className="email-content-tools">
            <button type="button" className="btn btn--ghost btn--sm" aria-expanded={showPreview} onClick={() => setShowPreview((value) => !value)}>
              {showPreview ? 'Hide preview' : 'Preview email'}
            </button>
            <button
              type="button"
              className="btn btn--ghost btn--sm"
              onClick={() => {
                if (
                  showRawHtml &&
                  !window.confirm(
                    'The visual editor may simplify custom HTML styles and layouts. Keep HTML mode to preserve an imported newsletter exactly. Switch to the visual editor?',
                  )
                )
                  return;
                setShowRawHtml((v) => !v);
              }}
            >
              {showRawHtml ? 'Use Editor' : 'Edit HTML'}
            </button>
          </div>
        </div>
        {showPreview && (
          <div className="email-preview">
            <div className="email-composer-actions">
              <button type="button" className="btn btn--ghost" aria-pressed={previewWidth === 'desktop'} onClick={() => setPreviewWidth('desktop')}>
                Desktop preview
              </button>
              <button type="button" className="btn btn--ghost" aria-pressed={previewWidth === 'mobile'} onClick={() => setPreviewWidth('mobile')}>
                Mobile preview
              </button>
            </div>
            <p>
              <strong>{subject || 'Email subject'}</strong>
              <br />
              {previewText}
            </p>
            <iframe
              title="Email content preview"
              sandbox=""
              referrerPolicy="no-referrer"
              style={{ width: previewWidth === 'mobile' ? 375 : '100%', maxWidth: '100%' }}
              srcDoc={emailPreviewHtml(html)}
            />
            <p className="text-muted">Email apps may display formatting differently. Send a test email before sending your newsletter.</p>
          </div>
        )}
        {showRawHtml && <p className="text-muted">Paste an HTML newsletter or edit the source. HTML mode preserves custom styles and layouts.</p>}
        {showRawHtml ? (
          <textarea value={html} onChange={(e) => setHtml(e.target.value)} rows={12} placeholder="Edit raw HTML…" style={{ width: '100%' }} />
        ) : (
          <TiptapEmailEditor value={html} onChange={setHtml} teamId={teamId} />
        )}

        {/* Audience */}
        <div className="email-send-settings">
          <section className="email-settings-section" aria-labelledby="email-audience-heading">
            <div className="email-section-heading">
              <h3 id="email-audience-heading">Audience</h3>
              <span className="text-muted" style={{ fontSize: '0.9rem' }}>
                {audienceLabel}
                {' · '}
                {audienceLoading ? 'estimating…' : audienceCount != null ? `${audienceCount} members` : '—'}
                {audienceErr && <span style={{ color: 'var(--danger)', marginLeft: 6 }}>{audienceErr}</span>}
              </span>
            </div>
            {audienceMode === 'booking' && audienceStats && (
              <p className="text-muted" style={{ marginTop: 0 }}>
                Eligible {audienceStats.eligible ?? audienceStats.count} · deduplicated {audienceStats.deduplicated || 0} · suppressed{' '}
                {audienceStats.suppressed || 0} · excluded {audienceStats.excluded || 0} · final {audienceStats.final ?? audienceStats.count}
              </p>
            )}

            <div
              style={{
                display: 'flex',
                gap: '0.5rem',
                flexWrap: 'wrap',
                marginBottom: '0.75rem',
              }}
            >
              {[
                ['tags', 'Tags'],
                ['all', 'All members'],
                ['member', 'One member'],
                ...(bookingAudienceOptions.length ? [['booking', 'Event/course bookers']] : []),
              ].map(([mode, label]) => (
                <button
                  key={mode}
                  type="button"
                  className={`btn ${audienceMode === mode ? 'btn--primary' : 'btn--secondary'} btn--sm`}
                  aria-pressed={audienceMode === mode}
                  onClick={() => {
                    setAudienceMode(mode);
                    if (mode !== 'tags') clearTags();
                    if (mode !== 'member') {
                      setSelectedMemberId('');
                      setMemberSearch('');
                    }
                  }}
                >
                  {label}
                </button>
              ))}
            </div>

            {audienceMode === 'booking' && (
              <div>
                <label>
                  <span className="form-label">Booking audience</span>
                  <select
                    className="input"
                    value={Math.max(
                      0,
                      bookingAudienceOptions.findIndex(
                        (option) =>
                          option.value?.sourceType === selectedBookingAudience?.sourceType &&
                          option.value?.sourceId === selectedBookingAudience?.sourceId &&
                          (option.value?.occurrenceId || '') === (selectedBookingAudience?.occurrenceId || ''),
                      ),
                    )}
                    onChange={(event) => setSelectedBookingAudience(bookingAudienceOptions[Number(event.target.value)]?.value || null)}
                  >
                    {bookingAudienceOptions.map((option, index) => (
                      <option key={`${option.label}-${index}`} value={index}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="checkbox-row">
                  <input
                    type="checkbox"
                    checked={selectedBookingAudience?.includeCancelled === true}
                    onChange={(event) =>
                      setSelectedBookingAudience((current) => ({
                        ...(current || {}),
                        includeCancelled: event.target.checked,
                      }))
                    }
                  />{' '}
                  Include cancelled and fully refunded bookings
                </label>
              </div>
            )}

            {audienceMode === 'member' && (
              <div>
                <label>
                  <span className="form-label">Find member</span>
                  <input
                    type="text"
                    value={memberSearch}
                    onChange={(event) => setMemberSearch(event.target.value)}
                    placeholder="Search members by name or email"
                  />
                </label>

                {selectedMember && (
                  <div className="text-muted" style={{ fontSize: '0.9rem', marginTop: '0.5rem' }}>
                    Selected: <strong>{memberLabel(selectedMember)}</strong>
                  </div>
                )}

                <div
                  style={{
                    maxHeight: 220,
                    overflowY: 'auto',
                    marginTop: '0.5rem',
                    border: '1px solid #e5e7eb',
                    borderRadius: 12,
                  }}
                >
                  {filteredMembers.length === 0 ? (
                    <div className="text-muted" style={{ padding: '0.75rem' }}>
                      No members match that search.
                    </div>
                  ) : (
                    filteredMembers.map((member) => (
                      <label
                        key={member.id}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.6rem',
                          padding: '0.65rem 0.75rem',
                          borderBottom: '1px solid #f3f4f6',
                          cursor: 'pointer',
                        }}
                      >
                        <input
                          type="radio"
                          name="email-recipient-member"
                          checked={selectedMemberId === member.id}
                          onChange={() => setSelectedMemberId(member.id)}
                        />
                        <span>{memberLabel(member)}</span>
                      </label>
                    ))
                  )}
                </div>
              </div>
            )}

            {audienceMode === 'tags' && (
              <>
                {Object.keys(grouped).length === 0 ? (
                  <p className="text-muted" style={{ marginTop: '0.5rem' }}>
                    No tags created yet. You can still send a test email from this modal.
                  </p>
                ) : (
                  <div style={{ display: 'grid', gap: '0.75rem' }}>
                    {Object.entries(grouped).map(([category, list]) => (
                      <div key={category} className="tag-category-block">
                        <h4 className="tag-category-heading" style={{ marginBottom: '0.35rem' }}>
                          {category}
                        </h4>
                        <div
                          className="tags-list"
                          style={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: '0.5rem',
                          }}
                        >
                          {list.map((t) => {
                            const tagName = typeof t?.name === 'string' ? t.name : 'Unnamed Tag';
                            const checked = !!selectedTags[category]?.has(tagName);
                            return (
                              <label
                                key={`${category}:${tagName}`}
                                className="tag-chip"
                                style={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: '0.4rem',
                                  cursor: 'pointer',
                                }}
                                title={t?.selfAssignable ? 'Self-assignable' : ''}
                              >
                                <input type="checkbox" checked={checked} onChange={() => toggleTag(category, tagName)} />
                                <span>{tagName}</span>
                                {t?.selfAssignable && (
                                  <span aria-label="Self-assignable" title="Self-assignable">
                                    🔓
                                  </span>
                                )}
                                {t?.multiAssignable && (
                                  <span aria-label="Multi-assignable" title="Multi-assignable">
                                    ＋
                                  </span>
                                )}
                              </label>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </section>

          <section className="email-settings-section" aria-labelledby="email-delivery-heading">
            <h3 id="email-delivery-heading">Delivery</h3>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button
                type="button"
                className={`btn ${deliveryMode === 'now' ? 'btn--primary' : 'btn--secondary'} btn--sm`}
                aria-pressed={deliveryMode === 'now'}
                onClick={() => setDeliveryMode('now')}
              >
                Send now
              </button>
              <button
                type="button"
                className={`btn ${deliveryMode === 'absolute' ? 'btn--primary' : 'btn--secondary'} btn--sm`}
                aria-pressed={deliveryMode === 'absolute'}
                onClick={() => setDeliveryMode('absolute')}
              >
                Schedule date
              </button>
              {audienceMode === 'booking' && (
                <button
                  type="button"
                  className={`btn ${deliveryMode === 'relative' ? 'btn--primary' : 'btn--secondary'} btn--sm`}
                  aria-pressed={deliveryMode === 'relative'}
                  onClick={() => setDeliveryMode('relative')}
                >
                  Before event/session
                </button>
              )}
            </div>
            {deliveryMode === 'absolute' && (
              <label>
                <span className="form-label">Send at</span>
                <input className="input" type="datetime-local" value={scheduledAt} onChange={(event) => setScheduledAt(event.target.value)} />
              </label>
            )}
            {deliveryMode === 'relative' && (
              <label>
                <span className="form-label">Days before the selected occurrence or session</span>
                <input className="input" type="number" min="0" max="365" value={daysBefore} onChange={(event) => setDaysBefore(event.target.value)} />
              </label>
            )}
          </section>
        </div>

        <details className="email-test-section">
          <summary>Send a test email</summary>
          <p className="text-muted">Check how your message looks in your inbox before sending.</p>
          <div className="email-test-fields">
            <label>
              <span className="form-label">Send a test to</span>
              <input type="email" placeholder="me@example.com" value={testEmail} onChange={(e) => setTestEmail(e.target.value)} />
            </label>
            <SubmitButton type="button" onClick={handleTestSend} label="Send Test" variant="secondary" disabled={loading} />
          </div>
        </details>

        <div className="form-actions email-composer-footer">
          <SubmitButton variant="ghost" type="button" onClick={onClose} label="Close" />
          <SubmitButton
            type="button"
            variant="primary"
            onClick={handleSendNow}
            label={loading ? 'Working…' : deliveryMode === 'now' ? 'Send Now' : 'Schedule Email'}
            disabled={sendDisabled || (deliveryMode === 'absolute' && !scheduledAt)}
          />
        </div>
      </div>
    </div>
  );
}
